import React, { useState, useEffect } from 'react';
import '../Properties/property.scss';
import SearchDropdown from '../../Components/Dropdown/dropdown';
import PropTypes from 'prop-types';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PropertyLayout from './layout';
import useApiState from '../../Hooks/useApiState';

const JURISDICTIONS = [
  { id: 'Corporation', name: 'Corporation' },
  { id: 'Town Muncipality', name: 'Town Muncipality' },
  { id: 'Muncipality', name: 'Muncipality' },
  { id: 'Panchayat', name: 'Panchayat' },
];

// const INDIAN_STATES = [
//   'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
//   'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
//   'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
//   'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
//   'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
//   'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands',
//   'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir',
//   'Ladakh', 'Lakshadweep', 'Puducherry',
// ];

const initialState = {
  address1: '',
  address2: '',
  area: '',
  taluk: '',
  district: '',
  pin: '',
  city: '',
  state: '',
  country: 'India',
  addressDescription: '',
  location: { mapLink: '', latitude: '', longitude: '' },
  jurisdiction: '',
};

function PropertyAddressForm({ prevStep, onNext, onSave, onBackToDashboard, initialValues, totalSteps, systemId, isView = false }) {
  // Ensure country always defaults to 'India' if not set
  const mergedInitial = { ...initialState, ...initialValues };
  if (!mergedInitial.country) mergedInitial.country = 'India';
  if (!mergedInitial.location) mergedInitial.location = { mapLink: '', latitude: '', longitude: '' };
  const [form, setForm] = useState(mergedInitial);
  const [errors] = useState({});

  // Use the API state hook
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save address details',
  });

  useEffect(() => {
    if (initialValues) {
      setForm((prev) => {
        const updated = { ...prev, ...initialValues };
        if (!updated.country) updated.country = 'India';
        if (!updated.location) updated.location = { mapLink: '', latitude: '', longitude: '' };
        return updated;
      });
    }
  }, [initialValues]);

  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    const { name, value } = event.target;
    if (name === 'location') {
      let locationObj = { mapLink: '', latitude: '', longitude: '' };
      if (/^(http|https):\/\//.test(value)) {
        locationObj.mapLink = value;
      } else {
        // Try to extract two numbers (lat, lng) from the string, allowing various separators
        // eslint-disable-next-line no-useless-escape
        const match = value.match(/(-?\d{1,3}(?:\.\d+)?)[^\d-.]+(-?\d{1,3}(?:\.\d+)?)/);
        if (match) {
          locationObj.latitude = match[1];
          locationObj.longitude = match[2];
        } else {
          locationObj.mapLink = value;
        }
      }
      setForm((prev) => ({ ...prev, location: locationObj }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleJurisdictionChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    setForm((prev) => ({ ...prev, jurisdiction: event.target.value.id }));
  };

  // const handleStateChange = (event) => {
  //   setForm((prev) => ({ ...prev, state: event.target.value.id }));
  // };

  // const validate = () => {
  //   const errs = {};
  //   if (!form.address1 || !/^[a-zA-Z0-9 ,.-]+$/.test(form.address1)) errs.address1 = 'Required, alphanumeric';
  //   if (form.address2 && !/^[a-zA-Z0-9 ,.-]+$/.test(form.address2)) errs.address2 = 'Alphanumeric only';
  //   if (!/^\d{6}$/.test(form.pin)) errs.pin = '6 digit numeric required';
  //   // city, state, country are now optional
  //   if (!form.addressDescription) errs.addressDescription = 'Required';
  //   if (!form.location) errs.location = 'Required';
  //   if (!form.jurisdiction) errs.jurisdiction = 'Required';
  //   return errs;
  // };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // In view mode, just navigate without saving
    if (isView) {
      onNext(form);
      return;
    }

    // const errs = validate();
    // enable if error messages required
    // setErrors(errs);
    // if (Object.keys(errs).length === 0) {

    clearErrors();
    try {
      await submitData(async () => {
        // This simulates async processing like an API call
        return Promise.resolve();
      }, {
        successMessage: 'Address details saved successfully',
      });

      onNext(form);
    } catch (error) {
      // Error is already handled by useApiState
      console.error('Failed to save address details:', error);
    }
    // }
  };

  // Helper to save form data before navigating
  const handlePrev = () => {
    onSave(form); // Save form data only
    prevStep(); // Go to previous step
  };

  return (
    <PropertyLayout
      step={2}
      totalSteps={totalSteps}
      systemId={systemId}
      onBackToDashboard={onBackToDashboard}
    >
      <div className='card'>
        <h3 className='card-title'>
          <LocationOnIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Location Details
        </h3>
        <h3 className='header-title'>Property Address</h3>

        {/* Display API errors */}
        {submitError && (
          <div className="error-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
          }}>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className='form-group'>
            <label>Address Line 1</label>
            <input
              type='text'
              name='address1'
              value={form.address1}
              onChange={handleChange}
              placeholder='House/Flat/Plot Number, Building Name, Street Name'
              disabled={isSubmitting || isView}
              readOnly={isView}
            />
            {errors.address1 && <span className='error'>{errors.address1}</span>}
          </div>
          <div className='form-group'>
            <label>Address Line 2 (optional)</label>
            <input
              type='text'
              name='address2'
              value={form.address2}
              onChange={handleChange}
              placeholder='Area, Landmark, Near...'
              disabled={isSubmitting || isView}
              readOnly={isView}
            />
            {errors.address2 && <span className='error'>{errors.address2}</span>}
          </div>
          {/* New row for Area, Taluk, District */}
          <div className='form-row-three'>
            <div className='form-group'>
              <label>Area</label>
              <input
                type='text'
                name='area'
                value={form.area}
                onChange={handleChange}
                placeholder='e.g., Ajjarkad'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
            <div className='form-group'>
              <label>Taluk</label>
              <input
                type='text'
                name='taluk'
                value={form.taluk}
                onChange={handleChange}
                placeholder='e.g., Udupi'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
            <div className='form-group'>
              <label>District</label>
              <input
                type='text'
                name='district'
                value={form.district}
                onChange={handleChange}
                placeholder='e.g., Udupi'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
          </div>
          <div className='form-row-three'>
            <div className='form-group'>
              <label>City</label>
              <input
                type='text'
                name='city'
                value={form.city}
                onChange={handleChange}
                placeholder='Enter city'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
              {errors.city && <span className='error'>{errors.city}</span>}
            </div>
            <div className='form-group'>
              <label>Postal Code / PIN</label>
              <input
                type='text'
                name='pin'
                maxLength={6}
                value={form.pin}
                onChange={handleChange}
                placeholder='Enter PIN code'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
              {errors.pin && <span className='error'>{errors.pin}</span>}
            </div>
            <div className='form-group'>
              <label>State/Country</label>
              <input
                type='text'
                name='country'
                value={'Karnataka/India'}
                readOnly
                disabled
                placeholder='Country'
              />
              {errors.country && <span className='error'>{errors.country}</span>}
            </div>
          </div>
          <h3 className='header-title' style={{ marginTop: 24 }}>Additional Location Details</h3>
          <div className='form-group'>
            <label>Complete Address Description</label>
            <textarea
              name='addressDescription'
              value={form.addressDescription}
              onChange={handleChange}
              placeholder='Provide a complete address description including any specific directions or landmarks'
              disabled={isSubmitting || isView}
              readOnly={isView}
            />
            {errors.addressDescription && <span className='error'>{errors.addressDescription}</span>}
          </div>
          <div className='form-row'>
            <div className='form-group'>
              <label>Google Maps Location</label>
              <input
                type='text'
                name='location'
                value={form.location.mapLink || (form.location.latitude && form.location.longitude ? `${form.location.latitude}, ${form.location.longitude}` : '')}
                onChange={handleChange}
                placeholder='Paste Google Maps coordinates or link'
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
              <div className='field-note'>You can paste coordinates (lat, lng) or a Google Maps share link</div>
              {errors.location && <span className='error'>{errors.location}</span>}
            </div>
            <div className='form-group'>
              <SearchDropdown
                label='Jurisdiction'
                options={JURISDICTIONS}
                value={JURISDICTIONS.find((event) => event.id === form.jurisdiction) || ''}
                onChange={handleJurisdictionChange}
                placeholder='Select jurisdiction'
                searchBox={false}
                disabled={isSubmitting || isView}
              />
              {errors.jurisdiction && <span className='error'>{errors.jurisdiction}</span>}
            </div>
          </div>
          <div className='form-actions'>
            <button
              type='button'
              className='btn secondary'
              onClick={handlePrev}
              disabled={isSubmitting}
            >
              Previous
            </button>
            {isView ? (
              <button
                type='button'
                className='btn primary'
                onClick={() => onNext(form)}
              >
                Next
              </button>
            ) : (
              <button
                type='submit'
                className='btn primary'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Next'}
              </button>
            )}
          </div>
        </form>
      </div>
    </PropertyLayout>
  );
}

PropertyAddressForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isEdit: PropTypes.bool,
  isView: PropTypes.bool,
};

export default PropertyAddressForm;
